import { create } from 'zustand';
import { getState, useState } from '@/framework';
import { IExportState, StateType } from '@/version-types';

export interface IExternalMethodsProps {
    cancelClickHandler: () => Promise<void>;
    confirmClickHandler: () => Promise<void>;
    optimizeClickHandler: () => void;
    beforeConfirm: (from: string) => Promise<boolean>;
}

export interface ImageCutoutInfo {
    originImage: string;
    resultImage: string;
    isCutoutMainLayer: boolean;
}

export interface IExternalParamsProps {
    leftFreeNum: number;
    freeNum: number;
    isFree: boolean;
    isIntl?: boolean;
    isSilentMode: boolean;
    numLeft: number;
    isInternalUserAndNotVipAndNotLeftNum?: boolean;
    isInternalUserVip?: boolean;
    trackFunc?: string;
}

/** 状态 */
interface IState {
    isImageCutout: boolean;
    imageCutoutInfo: ImageCutoutInfo;
    externalMethods: IExternalMethodsProps;
    externalParams: IExternalParamsProps;
}

/** 动作 */
interface IAction {
    setIsImageCutout: (isImageCutout: boolean) => void;
    setImageCutoutInfo: (imageCutoutInfo: ImageCutoutInfo) => void;
    setExternalMethods: (externalMethods: IExternalMethodsProps) => void;
    setExternalParams: (externalParams: IExternalParamsProps) => void;
}

/** 存储类型 */
interface IStore extends IState, IAction {}

export const getImageCutoutInfo = () => {
    return {
        originImage: '',
        resultImage: '',
        isCutoutMainLayer: false,
    };
};
/** 初始化状态 */
export function getInitialState() {
    return {
        isImageCutout: false,
        imageCutoutInfo: getImageCutoutInfo(),
        externalMethods: {
            cancelClickHandler: async () => {},
            confirmClickHandler: async () => {},
            optimizeClickHandler: () => {},
            beforeConfirm: () => Promise.resolve(true),
        },
        externalParams: {
            leftFreeNum: 0,
            freeNum: 0,
            isFree: false,
            isIntl: false,
            isSilentMode: false,
            numLeft: 0,
            isInternalUserVip: false,
            trackFunc: '',
        },
    };
}

/** 创建存储 */
const createState = (identity: string) => {
    const store = create<IStore>((set, get) => ({
        ...getInitialState(),

        setIsImageCutout: (isImageCutout) => {
            set({ isImageCutout });
        },
        setImageCutoutInfo: (imageCutoutInfo) => {
            set({ imageCutoutInfo });
        },
        setExternalMethods: (externalMethods) => {
            set({ externalMethods });
        },
        setExternalParams: (externalParams) => {
            set({ externalParams });
        },
    }));

    return store;
};

/** 存储名称 */
const name = 'image-cutout-state';

/** 存储类型 */
type ModuleType = StateType<IStore, typeof name>;

/** hook 获取存储 */
export const useImageCutoutState = () => {
    return useState<ModuleType>()[name];
};

/** 通过 id 获取存储 */
export const getImageCutoutState = (identity: string) => {
    return getState<ModuleType>(identity)[name].getState();
};

const exportState: IExportState = {
    name,
    createState,
};

export default exportState;
